// LeadsFullUI.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Stack,
  useToast,
  Heading,
  Box,
  Flex,
  Button,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  IconButton,
  GridItem,
  Icon,
  Select,
  VStack,
  Input,
  Skeleton,
} from "@chakra-ui/react";
import {
  FiMoreVertical,
  FiUsers,
  FiEdit2,
} from "react-icons/fi";
import { CgAddR } from "react-icons/cg";
import { LuCar } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { TbSettings } from "react-icons/tb";
import { ImFilter } from "react-icons/im";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import axios from "axios";
import { InputGroup, InputLeftElement } from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";
const normalizeLeadStatus = (status) => {
  if (!status) return "New"; // Fresh lead
  if (status === "Pending") return "Pending";
  if (status === "Converted") return "Converted";
  if (status === "Rejected") return "Rejected";
  if (status === "Expired") return "Expired";
  return status;
};

/* ---------- static fallback data ---------- */
const staticBookings = [
  {
    id: "1",
    name: "Smith",
    mobileNo: "9252252515",
    date: "2025-05-05",
    price: "₹5200",
    pickUp: "Mumbai",
    destination: "New Delhi",
    car: "Sedan",
    status: "Processing",
  },
  {
    id: "2",
    name: "Smith",
    mobileNo: "9252252515",
    date: "2025-05-05",
    price: "₹5200",
    pickUp: "Mumbai",
    destination: "New Delhi",
    car: "Sedan",
    status: "Complete",
  },
  {
    id: "3",
    name: "Smith",
    mobileNo: "9252252515",
    date: "2025-05-05",
    price: "₹5200",
    pickUp: "Mumbai",
    destination: "New Delhi",
    car: "Sedan",
    status: "Pending",
  },
  {
    id: "4",
    name: "Smith",
    mobileNo: "9252252515",
    date: "2025-05-05",
    price: "₹5200",
    pickUp: "Mumbai",
    destination: "New Delhi",
    car: "Sedan",
    status: "Cancelled",
  },
];

const statusColor = (status) => {
  switch (status) {
    case "New":
      return "blue";
    case "Pending":
      return "orange";
    case "Converted":
      return "green";
    case "Rejected":
    case "Expired":
      return "red";
    default:
      return "gray";
  }
};

const safeStr = (v) =>
  v === undefined || v === null ? "" : String(v).trim().toLowerCase();

const normalizeStatusFilterValue = (value) => {
  const normalized = safeStr(value);

  if (normalized === "completed") return "complete";
  if (normalized === "in progress") return "processing";

  return normalized;
};

const isDateOnlyIsoString = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) ||
  /^\d{4}-\d{2}-\d{2}t00:00:00z$/.test(String(value || "").trim().toLowerCase());

const getLocalDateFromIsoDateOnly = (value) => {
  const raw = String(value || "").trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const isoUtcMidnightMatch = raw.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})t00:00:00z$/i);
  if (isoUtcMidnightMatch) {
    const [, year, month, day] = isoUtcMidnightMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return null;
};

const getDateSearchText = (dateValue) => {
  const raw = safeStr(dateValue);
  if (!raw || raw === "-") return "";

  const candidates = new Set([raw]);

  // ISO / backend Date formats
  const parsed = isDateOnlyIsoString(dateValue)
    ? getLocalDateFromIsoDateOnly(dateValue)
    : new Date(dateValue);
  if (!Number.isNaN(parsed.getTime())) {
    candidates.add(
      parsed
        .toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
        .toLowerCase(),
    );
    candidates.add(String(parsed.getFullYear()));
    candidates.add(String(parsed.getMonth() + 1));
    candidates.add(String(parsed.getMonth() + 1).padStart(2, "0"));
    candidates.add(String(parsed.getDate()));
    candidates.add(String(parsed.getDate()).padStart(2, "0"));
  }

  // Support dd/mm/yyyy and dd-mm-yyyy date strings
  const dmyMatch = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})/);
  if (dmyMatch) {
    const day = Number(dmyMatch[1]);
    const month = Number(dmyMatch[2]);
    let year = Number(dmyMatch[3]);
    if (year < 100) year += 2000;

    candidates.add(String(year));
    candidates.add(String(month));
    candidates.add(String(month).padStart(2, "0"));
    candidates.add(String(day));
    candidates.add(String(day).padStart(2, "0"));

    const normalized = new Date(year, month - 1, day);
    if (!Number.isNaN(normalized.getTime())) {
      candidates.add(
        normalized
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
          .toLowerCase(),
      );
    }
  }

  return Array.from(candidates).join(" ");
};

const getTimeSearchText = (timeValue, dateValue) => {
  const candidates = new Set();

  const addTimeVariants = (hour24, minute) => {
    if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return;
    if (hour24 < 0 || hour24 > 23 || minute < 0 || minute > 59) return;

    const hh24 = String(hour24).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    const period = hour24 >= 12 ? "pm" : "am";
    const hh12 = hour24 % 12 || 12;
    const hh12Padded = String(hh12).padStart(2, "0");

    candidates.add(`${hh24}:${mm}`);
    candidates.add(`${hh12}:${mm} ${period}`);
    candidates.add(`${hh12Padded}:${mm} ${period}`);
    candidates.add(`${hh12}:${mm}${period}`);
    candidates.add(`${hh12Padded}:${mm}${period}`);
    candidates.add(`${hh12} ${period}`);
    candidates.add(`${hh12Padded} ${period}`);
    candidates.add(period);
  };

  const rawTime = safeStr(timeValue);
  if (rawTime && rawTime !== "-") {
    candidates.add(rawTime);

    const time12Match = rawTime.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i);
    if (time12Match) {
      let hour = Number(time12Match[1]);
      const minute = Number(time12Match[2]);
      const period = time12Match[3].toLowerCase();
      if (period === "pm" && hour !== 12) hour += 12;
      if (period === "am" && hour === 12) hour = 0;
      addTimeVariants(hour, minute);
    }

    const time24Match = rawTime.match(/^(\d{1,2}):(\d{2})$/);
    if (time24Match) {
      addTimeVariants(Number(time24Match[1]), Number(time24Match[2]));
    }
  }

  const parsedDate = isDateOnlyIsoString(dateValue)
    ? getLocalDateFromIsoDateOnly(dateValue)
    : new Date(dateValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    addTimeVariants(parsedDate.getHours(), parsedDate.getMinutes());
  }

  return Array.from(candidates).join(" ");
};

const resolveBookingTypeFromRecord = (record = {}) => {
  const explicit = safeStr(record.bookingType || record.leadID?.bookingType);
  if (explicit.includes("custom")) return "custom";
  if (explicit.includes("package")) return "package";
  if (explicit.includes("regular") || explicit.includes("standard") || explicit.includes("normal")) {
    return "regular";
  }

  if (
    record.customBookingID ||
    record.customBookingId ||
    record.isCustomBooking ||
    record.leadID?.customBookingID ||
    record.leadID?.customBookingId
  ) {
    return "custom";
  }

  if (
    record.packageId ||
    record.packageID ||
    record.leadID?.packageId ||
    record.leadID?.packageID
  ) {
    return "package";
  }

  return "regular";
};

const SearchTh = React.memo(({ value, onChange, placeholder = "Search" }) => (
  <Th p={2}>
    <InputGroup size="sm">
      <InputLeftElement pointerEvents="none">
        <SearchIcon color="#8BA3CB" boxSize="3.5" />
      </InputLeftElement>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
        fontSize="xs"
        borderRadius="8px"
        bg="#fff"
        border="none"
        pl="2rem"
        _placeholder={{ color: "#8BA3CB" }}
        _focus={{
          bg: "white",
          boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.3)",
        }}
      />
    </InputGroup>
  </Th>
));

const Leads = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState("Leads"); // Bookings | Leads | Pending
  const [usingApi, setUsingApi] = useState(false);
  const [apiRows, setApiRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const isApiDrivenTab =
    selectedTab === "Bookings" ||
    selectedTab === "Leads" ||
    selectedTab === "Pending" ||
    selectedTab === "Custom Bookings";

  // Check if vehicle is selected for booking
  const [vehicleSelected, setVehicleSelected] = useState(false);
  const [selectedVehicleData, setSelectedVehicleData] = useState(null);

  const [bookingStats, setBookingStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    incoming: 0,
    upcoming: 0,
  });
  const [customBookingStats, setCustomBookingStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    incoming: 0,
    upcoming: 0,
  });
  const [leadStats, setLeadStats] = useState({
    NewLead: 0,
    pending: 0,
    converted: 0,
    expired: 0,
    rejected: 0,
  });
  const [pendingTokenStats, setPendingTokenStats] = useState({
    total: 0,
  });

  const [staticRows, setStaticRows] = useState({
    Bookings: staticBookings,
    Leads: [],
    Pending: [],
    "Custom Bookings": [],
  });

  const [filters, setFilters] = useState({
    status: "",
    car: "",
    fromDate: "",
    toDate: "",
  });
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    mobile: "",
    date: "",
    time: "",
    price: "",
    pickUp: "",
    destination: "",
    car: "",
    bookingType: "",
    status: "",
  });
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const searchDebounceRef = useRef(null);


  const backendSearch = useMemo(() => {
    // Backend search does not reliably support date/year/time matching from column filters.
    // Keep those filters local and only send text filters that backend can resolve.
    const backendFilterKeys = [
      "name",
      "mobile",
      "pickUp",
      "destination",
      "car",
    ];

    return backendFilterKeys
      .map((key) => columnFilters[key])
      .filter((value) => String(value || "").trim())
      .join(" ")
      .trim();
  }, [columnFilters]);

  const hasActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).some((v) => String(v || "").trim()),
    [columnFilters],
  );

  const selectedBookingTypeFilter = useMemo(
    () => safeStr(columnFilters.bookingType),
    [columnFilters.bookingType],
  );

  const [backendPagination, setBackendPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1,
    limit: itemsPerPage,
  });

  useEffect(() => {
    const bookingData = localStorage.getItem("lead_booking_data");
    if (bookingData) {
      setVehicleSelected(true);
      setSelectedVehicleData(JSON.parse(bookingData));
      // Switch to Leads tab automatically
      setSelectedTab("Leads");
      // Show toast notification
      // toast({
      //   title: "Vehicle Selected",
      //   description: "Select a lead below or create a new one to continue booking",
      //   status: "info",
      //   duration: 5000,
      //   isClosable: true,
      // });
    }
  }, []);

  // debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(searchDebounceRef.current);
  }, [backendSearch, selectedBookingTypeFilter]);

  /* ---------- API GET functions ---------- */
  const fetchBookingsFromApi = async (
    page = 1,
    limit = 10,
    opts = {},
    updatePagination = true,
  ) => {
    const token = localStorage.getItem("prima_session");
    if (!token) throw new Error("Token not found");

    const params = {
      page,
      limit,
      ...(opts.status ? { status: opts.status } : {}),
      ...(opts.vehicleSegment ? { vehicleSegment: opts.vehicleSegment } : {}),
    };
    if (backendSearch) {
      params.search = backendSearch;
    }
    const searchParams = new URLSearchParams(params).toString();
    const url = `${SERVER}/management/bookings?${searchParams}`;

    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    if (updatePagination) {
      if (res.data?.pagination) {
        setBackendPagination(res.data.pagination);
      } else {
        // fallback if backend doesn't send pagination
        setBackendPagination({
          total: res.data?.data?.length || 0,
          currentPage,
          totalPages: 1,
          limit: itemsPerPage,
        });
      }
    }
    return res.data;
  };

  const fetchLeadsFromApi = async (page, limit) => {
    const token = localStorage.getItem("prima_session");
    if (!token) throw new Error("Token not found");

    const params = { page, limit };
    if (backendSearch) params.search = backendSearch;
    const qs = new URLSearchParams(params).toString();

    const url = `${SERVER}/management/leads?${qs}`;
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    return res.data;
  };

  const fetchLeadStats = async () => {
    const token = localStorage.getItem("prima_session");
    if (!token) return;

    const res = await axios.get(`${SERVER}/management/lead-stats`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });

    if (res.data?.data) {
      setLeadStats(res.data.data);
    }
    if (res.data?.pagination) {
      setBackendPagination(res.data.pagination);
    } else {
      // fallback if backend doesn't send pagination
      setBackendPagination({
        total: res.data?.data?.length || 0,
        currentPage,
        totalPages: 1,
        limit: itemsPerPage,
      });
    }
  };

  const fetchPendingTokenFromApi = async (page, limit) => {
    const token = localStorage.getItem("prima_session");
    if (!token) throw new Error("Token not found");

    const params = { page, limit };
    if (backendSearch) params.search = backendSearch;
    const qs = new URLSearchParams(params).toString();
    const url = `${SERVER}/management/pending-tokens?${qs}`;
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    if (res.data?.globalStats) {
      setPendingTokenStats(res.data.globalStats);
    }
    if (res.data?.pagination) {
      setBackendPagination(res.data.pagination);
    } else {
      // fallback if backend doesn't send pagination
      setBackendPagination({
        total: res.data?.data?.length || 0,
        currentPage,
        totalPages: 1,
        limit: itemsPerPage,
      });
    }
    return res.data.data ?? res.data;
  };

  const fetchCustomBookingsFromApi = async (page, limit) => {
    const token = localStorage.getItem("prima_session");
    if (!token) throw new Error("Token not found");

    const params = { page, limit };
    if (backendSearch) params.search = backendSearch;
    const qs = new URLSearchParams(params).toString();
    const url = `${SERVER}/management/custom-bookings?${qs}`;
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    if (res.data?.pagination) {
      setBackendPagination(res.data.pagination);
    } else {
      setBackendPagination({
        total: res.data?.data?.length || 0,
        currentPage,
        totalPages: 1,
        limit: itemsPerPage,
      });
    }
    return res.data;
  };

  /* ---------- effect: fetch when tab changes ---------- */
  useEffect(() => {
    let mounted = true;

    const doFetch = async () => {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("prima_session");
      if (!token) {
        setUsingApi(false);
        setLoading(false);
        return;
      }

      if (selectedTab === "Leads") {
        await fetchLeadStats();
      }

      try {
        if (selectedTab === "Bookings") {
          const shouldUseLocalBookingTypeFilter = Boolean(selectedBookingTypeFilter);
          const opts = {
            status: filters.status || undefined,
            vehicleSegment: filters.car || undefined,
          };

          let resp = null;
          let rows = [];

          if (shouldUseLocalBookingTypeFilter) {
            let fetchPage = 1;
            let totalPagesFromApi = 1;
            const aggregatedRows = [];

            do {
              const pageResp = await fetchBookingsFromApi(
                fetchPage,
                itemsPerPage,
                opts,
                false,
              );

              if (!resp) {
                resp = pageResp;
              }

              const pageRows = Array.isArray(pageResp?.data)
                ? pageResp.data
                : Array.isArray(pageResp)
                  ? pageResp
                  : [];

              aggregatedRows.push(...pageRows);

              totalPagesFromApi = Number(pageResp?.pagination?.totalPages) || 1;
              fetchPage += 1;

              // Safety guard against unexpected backend pagination contracts.
              if (fetchPage > 500) break;
            } while (fetchPage <= totalPagesFromApi);

            rows = aggregatedRows;
          } else {
            resp = await fetchBookingsFromApi(
              currentPage,
              itemsPerPage,
              opts,
            );

            rows = Array.isArray(resp?.data)
              ? resp.data
              : Array.isArray(resp)
                ? resp
                : [];
          }

          if (resp.globalStats) {
            setBookingStats(resp.globalStats);
          }

          if (!mounted) return;
          const normalized = rows.map((r) => {
            // Calculate price with fallback chain
            let price = "-";
            if (r.fareDetails?.total && r.fareDetails.total > 0) {
              price = `₹${r.fareDetails.total}`;
            } else if (r.leadID?.estimatedFare && r.leadID.estimatedFare > 0) {
              price = `₹${r.leadID.estimatedFare}`;
            } else if (r.estimatedFare && r.estimatedFare > 0) {
              price = `₹${r.estimatedFare}`;
            } else if (r.price) {
              price = r.price;
            }

            return {
              id:
                r.bookingID ??
                r.customBookingID ??
                r.leadID ??
                r._id ??
                r.id ??
                Math.random().toString(36).slice(2, 9),
              name: r.customer?.name ?? r.customerName ?? r.name ?? "-",
              mobileNo:
                r.customer?.mobile ?? r.customer?.mobileNo ?? r.mobileNo ?? "-",
              date:
                r.dateTime ?? r.pickupDateTime ?? r.date ?? r.createdAt ?? "-",
              price: price,
              pickUp:
                typeof (
                  r.pickupLocation ??
                  r.leadID?.fromLocation ??
                  r.pickUp
                ) === "object"
                  ? (r.pickupLocation ?? r.leadID?.fromLocation ?? r.pickUp)
                    ?.address || "-"
                  : (r.pickupLocation ??
                    r.leadID?.fromLocation ??
                    r.pickUp ??
                    "-"),
              destination:
                typeof (
                  r.dropLocation ??
                  r.leadID?.toLocation ??
                  r.destination
                ) === "object"
                  ? (r.dropLocation ?? r.leadID?.toLocation ?? r.destination)
                    ?.address || "-"
                  : (r.dropLocation ??
                    r.leadID?.toLocation ??
                    r.destination ??
                    "-"),
              car: r.vehicleSegment?.segmentName ?? r.car ?? "-",
              status: r.status,
              bookingType: resolveBookingTypeFromRecord(r),

              raw: r,
            };
          });
          const bookingTypeFiltered = shouldUseLocalBookingTypeFilter
            ? normalized.filter(
              (row) => safeStr(row.bookingType) === selectedBookingTypeFilter,
            )
            : normalized;

          setApiRows(bookingTypeFiltered);

          setBackendPagination({
            total: shouldUseLocalBookingTypeFilter
              ? bookingTypeFiltered.length
              : (resp.pagination?.total || bookingTypeFiltered.length),
            currentPage: shouldUseLocalBookingTypeFilter
              ? currentPage
              : (resp.pagination?.currentPage || currentPage),
            totalPages: shouldUseLocalBookingTypeFilter
              ? Math.max(Math.ceil(bookingTypeFiltered.length / itemsPerPage), 1)
              : (resp.pagination?.totalPages || Math.ceil(bookingTypeFiltered.length / itemsPerPage)),
            limit: shouldUseLocalBookingTypeFilter
              ? itemsPerPage
              : (resp.pagination?.limit || itemsPerPage),
          });

          setUsingApi(true);

          setUsingApi(true);
        } else if (selectedTab === "Leads") {
          const response = await fetchLeadsFromApi(currentPage, itemsPerPage);
          const data = response.data ?? response;
          const normalized = data.map((r) => ({
            id: r._id ?? r.id ?? Math.random().toString(36).slice(2, 9),
            name: r.customer?.name ?? r.customerName ?? r.name ?? "-",
            mobileNo:
              r.customer?.mobile ?? r.customer?.mobileNo ?? r.mobileNo ?? "-",
            createdDate:
              r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
            pickupDate:
              r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
            date:
              r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
            price: r.estimatedFare
              ? `₹${r.estimatedFare}`
              : r.fareDetails?.total
                ? `₹${r.fareDetails.total}`
                : (r.price ?? "-"),
            pickUp:
              typeof r.fromLocation === "object"
                ? r.fromLocation?.address || "-"
                : (r.fromLocation ?? "-"),
            destination:
              typeof r.toLocation === "object"
                ? r.toLocation?.address || "-"
                : (r.toLocation ?? "-"),
            car:
              r.vehicleSegment?.segmentName ??
              r.carType?.segmentName ??
              r.car ??
              "-",
            status: r.status ?? "New",
            bookingType: r.bookingType ?? "regular",
            raw: r,
          }));

          if (!mounted) return;
          setStaticRows((prev) => ({ ...prev, Leads: normalized }));
          setBackendPagination({
            total: response.pagination?.total || normalized.length,
            currentPage: response.pagination?.currentPage || currentPage,
            totalPages:
              response.pagination?.totalPages ||
              Math.ceil(normalized.length / itemsPerPage),
            limit: response.pagination?.limit || itemsPerPage,
          });
          setUsingApi(true);
        } else if (selectedTab === "Pending") {
          const data = await fetchPendingTokenFromApi(
            currentPage,
            itemsPerPage,
          );
          const normalized = data
            .filter((r) => {
              const status = r.status ?? "-";
              return safeStr(status).includes("pending");
            })
            .map((r) => ({
              id: r._id,
              leadId: r.lead?._id ?? r.booking?.leadID?._id ?? null,
              name: r.customer?.name ?? "-",
              mobileNo: r.customer?.mobile ?? "-",
              date:
                r.lead?.pickupDateTime ??
                r.booking?.dateTime ??
                r.booking?.leadID?.pickupDateTime ??
                "-",
              price: r.amount ? `₹${r.amount}` : "-",
              pickUp:
                typeof (
                  r.lead?.fromLocation ??
                  r.booking?.pickupLocation ??
                  r.booking?.leadID?.fromLocation
                ) === "object"
                  ? (
                    r.lead?.fromLocation ??
                    r.booking?.pickupLocation ??
                    r.booking?.leadID?.fromLocation
                  )?.address || "-"
                  : (r.lead?.fromLocation ??
                    r.booking?.pickupLocation ??
                    r.booking?.leadID?.fromLocation ??
                    "-"),
              destination:
                typeof (
                  r.lead?.toLocation ??
                  r.booking?.dropLocation ??
                  r.booking?.leadID?.toLocation
                ) === "object"
                  ? (
                    r.lead?.toLocation ??
                    r.booking?.dropLocation ??
                    r.booking?.leadID?.toLocation
                  )?.address || "-"
                  : (r.lead?.toLocation ??
                    r.booking?.dropLocation ??
                    r.booking?.leadID?.toLocation ??
                    "-"),
              car:
                r.lead?.vehicleSegment?.segmentName ??
                r.booking?.vehicleSegment?.segmentName ??
                "-",
              status: r.status ?? "-",
              bookingType: r.lead?.bookingType ?? r.booking?.bookingType ?? "regular",
              raw: r,
            }));

          if (!mounted) return;
          setStaticRows((prev) => ({ ...prev, Pending: normalized }));
          setUsingApi(true);
        } else if (selectedTab === "Custom Bookings") {
          const response = await fetchCustomBookingsFromApi(currentPage, itemsPerPage);
          if (response.globalStats) {
            setCustomBookingStats(response.globalStats);
          }
          const data = response.data ?? response;
          const normalized = data.map((r) => ({
            id: r._id ?? r.id ?? Math.random().toString(36).slice(2, 9),
            name: r.customer?.name ?? r.customerName ?? r.name ?? "-",
            mobileNo: r.customer?.mobile ?? r.customer?.mobileNo ?? r.mobileNo ?? "-",
            date: r.pickupDateTime ?? r.dateTime ?? r.date ?? r.createdAt ?? "-",
            price: r.pricingOutputs?.finalPrice
              ? `₹${r.pricingOutputs.finalPrice}`
              : r.pricingInputs?.baseRate
                ? `₹${r.pricingInputs.baseRate}`
                : (r.price ?? "-"),
            pickUp: r.pickupLocation ?? "-",
            destination: r.dropLocation ?? "-",
            car: r.vehicleSegment?.segmentName ?? "-",
            status: r.status ?? "-",
            bookingType: r.bookingType ?? "custom",
            raw: r,
          }));

          if (!mounted) return;
          setStaticRows((prev) => ({ ...prev, "Custom Bookings": normalized }));
          setBackendPagination({
            total: response.pagination?.total || normalized.length,
            currentPage: response.pagination?.currentPage || currentPage,
            totalPages:
              response.pagination?.totalPages ||
              Math.ceil(normalized.length / itemsPerPage),
            limit: response.pagination?.limit || itemsPerPage,
          });
          setUsingApi(true);
        }
      } catch (err) {
        console.warn(err);
        setError(err?.response?.data?.message ?? err.message ?? "API error");
        toast({
          title: "API error",
          description: "Data fetch failed",
          status: "warning",
          duration: 4000,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    doFetch();

    return () => {
      mounted = false;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [
    currentPage,
    itemsPerPage,
    filters.status,
    filters.car,
    selectedTab,
    refreshTrigger,
    backendSearch,
    selectedBookingTypeFilter,
  ]);

  // ---- current rows ---------- */
  // Combine API rows and static rows for filtering
  const currentAllRows = useMemo(() => {
    if (usingApi && selectedTab === "Bookings") {
      return apiRows;
    }
    const key = selectedTab === "Pending" ? "Pending" : selectedTab;
    return staticRows[key] ?? [];
  }, [usingApi, apiRows, staticRows, selectedTab]);

  // Filter rows according to status, car type, and date
  const filteredRowsLocal = useMemo(() => {
    // Apply column filters to all rows so multi-column AND filtering works consistently.
    let filtered = currentAllRows;

    if (Object.values(columnFilters).some((v) => v)) {
      filtered = filtered.filter((row) => {
        const name = safeStr(row.name);
        const mobile = safeStr(row.mobileNo);

        const dateStr = getDateSearchText(row.date);

        const timeStr = getTimeSearchText(row.time, row.date);

        const price = safeStr(row.price);
        const pickUp = safeStr(row.pickUp);
        const destination = safeStr(row.destination);
        const car = safeStr(row.car);
        const bookingType = safeStr(row.bookingType);
        const status = normalizeStatusFilterValue(row.status);
        const bookingTypeFilterValue = safeStr(columnFilters.bookingType);
        const statusFilterValue = normalizeStatusFilterValue(
          columnFilters.status,
        );

        const bookingTypeFilterMatch =
          !bookingTypeFilterValue || bookingType === bookingTypeFilterValue;
        const statusFilterMatch =
          !statusFilterValue || status === statusFilterValue;

        return (
          name.includes(safeStr(columnFilters.name)) &&
          mobile.includes(safeStr(columnFilters.mobile)) &&
          dateStr.includes(safeStr(columnFilters.date)) &&
          timeStr.includes(safeStr(columnFilters.time)) &&
          price.includes(safeStr(columnFilters.price)) &&
          pickUp.includes(safeStr(columnFilters.pickUp)) &&
          destination.includes(safeStr(columnFilters.destination)) &&
          car.includes(safeStr(columnFilters.car)) &&
          bookingTypeFilterMatch &&
          statusFilterMatch
        );
      });
    }

    const statusFilter = safeStr(filters.status);
    const carFilter = safeStr(filters.car);
    const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
    const toDate = filters.toDate ? new Date(filters.toDate) : null;

    return filtered.filter((row) => {
      // Status filter (case-insensitive)
      const statusMatch =
        !statusFilter || !row.status
          ? true
          : safeStr(row.status).includes(statusFilter);

      // Car filter (case-insensitive)
      const carMatch =
        !carFilter || !row.car ? true : safeStr(row.car).includes(carFilter);

      // Date filter
      let dateMatch = true;
      if (row.date) {
        const rowDate = new Date(row.date);
        if (isFinite(rowDate)) {
          if (fromDate) dateMatch = dateMatch && rowDate >= fromDate;
          if (toDate) dateMatch = dateMatch && rowDate <= toDate;
        }
      }

      return statusMatch && carMatch && dateMatch;
    });
  }, [currentAllRows, filters, columnFilters, usingApi, selectedTab, isApiDrivenTab]);

  // const totalRows = usingApi && selectedTab === "Bookings" ? backendPagination.total : filteredRowsLocal.length;
  const totalRows = isApiDrivenTab
    ? (hasActiveColumnFilters
      ? filteredRowsLocal.length
      : (backendPagination.total ?? 0))
    : filteredRowsLocal.length;

  // const totalPages = usingApi && selectedTab === "Bookings" ? backendPagination.totalPages || 1 : Math.max(Math.ceil(totalRows / itemsPerPage), 1);
  const totalPages = isApiDrivenTab
    ? (hasActiveColumnFilters
      ? Math.max(Math.ceil(totalRows / itemsPerPage), 1)
      : (backendPagination.totalPages ?? 1))
    : Math.max(Math.ceil(totalRows / itemsPerPage), 1);

  const pageRows = useMemo(() => {
    if (isApiDrivenTab && hasActiveColumnFilters) {
      return filteredRowsLocal.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      );
    }

    if (isApiDrivenTab) {
      return filteredRowsLocal; // already paginated by backend
    }

    // For API-driven bookings, don't slice - data is already paginated
    if (usingApi && selectedTab === "Bookings") {
      return filteredRowsLocal;
    }
    return filteredRowsLocal.slice(
      (currentPage - 1) * itemsPerPage,
      (currentPage - 1) * itemsPerPage + itemsPerPage,
    );
  }, [
    filteredRowsLocal,
    currentPage,
    itemsPerPage,
    usingApi,
    selectedTab,
    isApiDrivenTab,
    hasActiveColumnFilters,
  ]);

  /* ---------- handlers ---------- */
  const onDelete = async (row) => {
    const id = row.id;
    if (!window.confirm("Delete this item?")) return;
    try {
      const token = localStorage.getItem("prima_session");
      if (token && selectedTab === "Bookings") {
        await axios.delete(`${SERVER}/management/bookings/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setCurrentPage(1);
        toast({
          title: "Success",
          description: "Lead deleted successfully",
          status: "success",
          duration: 2000,
        });
        return;
      }
      setStaticRows((prev) => {
        const copy = { ...prev };
        const key = selectedTab === "Pending" ? "Pending" : selectedTab;
        copy[key] = copy[key].filter((r) => r.id !== id);
        return copy;
      });
      toast({
        title: "Success",
        description: "Lead removed from local list",
        status: "success",
        duration: 2000,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Delete failed",
        description: err?.response?.data?.message ?? err.message ?? "Error",
        status: "error",
      });
    }
  };

  /* ---------- Export CSV ---------- */
  const exportToCSV = (rowsToExport) => {
    if (!rowsToExport || rowsToExport.length === 0) {
      toast({
        title: "No data",
        description: "Kuch export karne ke liye nahi hai",
        status: "info",
        duration: 2500,
      });
      return;
    }

    const headers = [
      "S.No",
      "Name",
      "Mobile No",
      "Date",
      "Price",
      "Pick Up",
      "Destination",
      "Car",
      "Status",
    ];
    const csvRows = [headers.join(",")];
    rowsToExport.forEach((r, idx) => {
      const line = [
        idx + 1,
        `"${(r.name ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.mobileNo ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.date ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.price ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.pickUp ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.destination ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.car ?? "").toString().replace(/"/g, '""')}"`,
        `"${(r.status ?? "").toString().replace(/"/g, '""')}"`,
      ];
      csvRows.push(line.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `export_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleActionExportCurrent = () => {
    exportToCSV(pageRows);
  };
  const handleActionExportAll = () => {
    exportToCSV(usingApi ? apiRows : filteredRowsLocal);
  };
  const handleRefresh = () => {
    setCurrentPage(1);
    toast({ title: "Refreshed", status: "success", duration: 1500 });
  };

  /* ---------- render UI ---------- */
  return (
    <Stack w={"100%"} justifyContent={"center"} spacing={10} p={6}>
      <Flex justifyContent={"space-between"} alignItems={"center"} mb={0}>
        <BreadCrumbMaker items={[{ link: "#", title: "Bookings" }]} />
        <Flex gap={2}>
          <Button
            onClick={() => {
              // Normal flow - go to search
              navigate("/bookings/lead/search");
            }}
            size={"sm"}
            borderRadius={5}
            px={10}
            fontWeight={0}
            py={5}
          >
            <Flex gap={1} alignItems={"center"}>
              <CgAddR />{" "}
              <Text>
                {selectedTab === "Leads" ? "Add Booking" : "Add Booking"}
              </Text>
            </Flex>
          </Button>
        </Flex>
      </Flex>

      <Box my="0">
        <Grid
          gridGap={6}
          templateColumns={{
            base: "repeat(2, 1fr)",
            md: "repeat(4, minmax(250px, 1fr))",
          }}
        >
          <GridItem key="Upcoming">
            <Box
              bg="white"
              p={4}
              rounded="md"
              textAlign="center"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
              border={"1px solid #D0D5DD"}
            >
              <Box
                bg={"#FFF5D9"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FiUsers} boxSize={6} color={"#FFBB38"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Pending
                </Text>
                <Text fontSize="25px">
                  {selectedTab === "Bookings"
                    ? bookingStats.pending
                    : selectedTab === "Custom Bookings"
                      ? customBookingStats.pending
                      : selectedTab === "Pending"
                        ? pendingTokenStats.total
                        : leadStats.NewLead}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem key="Confirm">
            <Box
              bg="white"
              p={4}
              rounded="md"
              textAlign="center"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
              border={"1px solid #D0D5DD"}
            >
              <Box
                bg={"#E7EDFF"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={LuCar} boxSize={6} color={"#396AFF"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Confirm
                </Text>
                <Text fontSize="25px">
                  {selectedTab === "Custom Bookings"
                    ? customBookingStats.confirmed
                    : bookingStats.confirmed}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem key="Ongoing">
            <Box
              bg="white"
              p={4}
              rounded="md"
              textAlign="center"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
              border={"1px solid #D0D5DD"}
            >
              <Box
                bg={"#FFE0EB"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={LuCar} boxSize={6} color={"#FF82AC"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Incoming
                </Text>
                <Text fontSize="25px">
                  {selectedTab === "Custom Bookings"
                    ? customBookingStats.incoming
                    : bookingStats.incoming}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem key="Completed">
            <Box
              bg="white"
              p={4}
              rounded="md"
              textAlign="center"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
              border={"1px solid #D0D5DD"}
            >
              <Box
                bg={"#DCFAF8"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={LuCar} boxSize={6} color={"#16DBCC"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Upcoming
                </Text>
                <Text fontSize="25px">
                  {selectedTab === "Custom Bookings"
                    ? customBookingStats.upcoming
                    : bookingStats.upcoming}
                </Text>
              </Flex>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      <Flex gap={2}>
        <Button
          size="sm"
          borderRadius="10px"
          px={5}
          height={"40px"}
          border={"1px solid #D0D5DD"}
          bg={selectedTab === "Leads" ? "#0F60FF" : "white"}
          color={selectedTab === "Leads" ? "white" : "#718EBF"}
          onClick={() => {
            setSelectedTab("Leads");
            setCurrentPage(1);
          }}
        >
          Leads
        </Button>
        <Button
          size="sm"
          borderRadius="10px"
          px={5}
          height={"40px"}
          border={"1px solid #D0D5DD"}
          bg={selectedTab === "Pending" ? "#0F60FF" : "white"}
          color={selectedTab === "Pending" ? "white" : "#718EBF"}
          onClick={() => {
            setSelectedTab("Pending");
            setCurrentPage(1);
          }}
        >
          Pending Token
        </Button>
        <Button
          size="sm"
          borderRadius="10px"
          px={5}
          height={"40px"}
          border={"1px solid #D0D5DD"}
          bg={selectedTab === "Bookings" ? "#0F60FF" : "white"}
          color={selectedTab === "Bookings" ? "white" : "#718EBF"}
          onClick={() => {
            setSelectedTab("Bookings");
            setCurrentPage(1);
            // Auto-refresh when switching to Bookings tab
            setRefreshTrigger((prev) => prev + 1);
          }}
        >
          Bookings
        </Button>
        {/* Commented out Custom Bookings tab */}
        {/* <Button
          size="sm"
          borderRadius="10px"
          px={5}
          height={"40px"}
          border={"1px solid #D0D5DD"}
          bg={selectedTab === "Custom Bookings" ? "#0F60FF" : "white"}
          color={selectedTab === "Custom Bookings" ? "white" : "#718EBF"}
          onClick={() => {
            setSelectedTab("Custom Bookings");
            setCurrentPage(1);
          }}
        >
          Custom Bookings
        </Button> */}
      </Flex>

      {/* Controls */}
      <Flex
        justifyContent={"space-between"}
        flexDirection={{ base: "column", md: "row" }}
      >
        <Heading fontSize={"xl"} py={1} color={"#333B69"}>
          {selectedTab} List
        </Heading>

        <Flex w="100%" maxW="400px" gap={3} justifyContent={"flex-end"}>
          {/* Search removed on purpose */}

          {/* placeholder for Sort (removed) - keep visual same */}
          {/* <Flex gap={2} alignItems="center" justifyContent="center" w="100%" maxW="91px" bg="white" px={3} h="40px" borderRadius={"13"} cursor="default">
            <TbSettings color="#718EBF" fontSize="1rem" />
            <Text fontSize="sm" color="#718EBF">Sort</Text>
          </Flex> */}

          <Flex
            gap={2}
            alignItems="center"
            justifyContent="center"
            w="110px"
            h="40px"
            bg="white"
            border="1px solid #D0D5DD"
            borderRadius="13px"
            cursor="pointer"
            onClick={() => { setShowColumnSearch(!showColumnSearch); }}
          >
            <Text fontSize="sm" color="#718EBF">
              Search
            </Text>
          </Flex>

          {/* Filter menu (before Action) */}
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              w="110px"
              h="40px"
              bg="white"
              border="1px solid #D0D5DD"
              borderRadius="13px"
              color="#718EBF"
              fontSize="sm"
              fontWeight="400"
              _hover={{ bg: "white" }}
              _active={{ bg: "white" }}
              _focus={{ boxShadow: "none" }}
            >
              <Flex gap={2} alignItems="center" justifyContent="center">
                <ImFilter color="#718EBF" fontSize="1rem" />
                <Text fontSize="sm" color="#718EBF">
                  Filter
                </Text>
              </Flex>
            </MenuButton>

            <MenuList minW="250px" p={4}>
              <VStack align="stretch" spacing={4}>
                {selectedTab === "Bookings" && (
                  <Box>
                    <Text fontSize="sm" mb={2} fontWeight="500">
                      Assignment Status
                    </Text>
                    <Select
                      size="sm"
                      placeholder="All"
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      <option value="Assigned">Assigned</option>
                      <option value="Unassigned">Unassigned</option>
                    </Select>
                  </Box>
                )}

                <Box>
                  <Text fontSize="sm" mb={2} fontWeight="500">
                    Car Type
                  </Text>
                  <Select
                    size="sm"
                    placeholder="All"
                    value={filters.car}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, car: e.target.value }))
                    }
                  >
                    <option value="">All</option>
                    <option value="Sedan">Sedan</option>
                    <option value="SUV">SUV</option>
                    <option value="Hatchback">Hatchback</option>
                  </Select>
                </Box>

                <Box>
                  <Text fontSize="sm" mb={2} fontWeight="500">
                    From Date
                  </Text>
                  <Input
                    size="sm"
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, fromDate: e.target.value }))
                    }
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" mb={2} fontWeight="500">
                    To Date
                  </Text>
                  <Input
                    size="sm"
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, toDate: e.target.value }))
                    }
                  />
                </Box>

                <Flex gap={2} justifyContent="flex-end" pt={2}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setFilters({ status: "", car: "", fromDate: "", toDate: "" })
                    }
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => {
                      setCurrentPage(1);
                    }}
                  >
                    Apply
                  </Button>
                </Flex>
              </VStack>
            </MenuList>
          </Menu>

          {/* Action menu (after Filter) */}
          <Menu>
            <MenuButton
              as={Button}
              w="110px"
              h="40px"
              bg="white"
              border="1px solid #D0D5DD"
              borderRadius="13px"
              color="#718EBF"
              fontSize="sm"
              fontWeight="400"
              _hover={{ bg: "white" }}
              _active={{ bg: "white" }}
              _focus={{ boxShadow: "none" }}
            >
              <Flex gap={2} alignItems="center" justifyContent="center">
                <TbSettings color="#718EBF" fontSize="1.3rem" />
                <Text fontSize="sm" color="#718EBF">
                  Action
                </Text>
              </Flex>
            </MenuButton>

            <MenuList>
              <MenuItem onClick={handleActionExportCurrent}>
                Export current page (CSV)
              </MenuItem>
              <MenuItem onClick={handleActionExportAll}>
                Export all (visible)
              </MenuItem>
              <MenuItem onClick={handleRefresh}>Refresh</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      {/* Table */}
      <Box
        p={7}
        bg={"white"}
        overflowX={"auto"}
        borderRadius={"3xl"}
        border={"1px solid #D0D5DD"}
      >
        <Table size="sm" bg={"white"} fontSize={"small"}>
          <Thead>
            <Tr>
              <Th>S.No</Th>
              <Th>Name</Th>
              <Th>Mobile No.</Th>
              <Th>Date</Th>
              <Th>Time</Th>
              <Th>Price</Th>
              <Th>Pick Up</Th>
              <Th>Destination</Th>
              <Th>Car</Th>
              <Th>Booking Type</Th>
              <Th>Status</Th>
              {selectedTab !== "Bookings" && <Th>Action</Th>}
            </Tr>
            {/* Column Filter Row */}
            {showColumnSearch && (
              <Tr bg="gray.50">
                <Th></Th>
                <SearchTh
                  value={columnFilters.name}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, name: e.target.value })
                  }
                />

                <SearchTh
                  value={columnFilters.mobile}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, mobile: e.target.value })
                  }
                />

                <SearchTh
                  value={columnFilters.date}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, date: e.target.value })
                  }
                />

                <SearchTh
                  value={columnFilters.time}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, time: e.target.value })
                  }
                />

                <SearchTh
                  value={columnFilters.price}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, price: e.target.value })
                  }
                />

                <SearchTh
                  value={columnFilters.pickUp}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, pickUp: e.target.value })
                  }
                />

                <SearchTh
                  value={columnFilters.destination}
                  onChange={(e) =>
                    setColumnFilters({
                      ...columnFilters,
                      destination: e.target.value,
                    })
                  }
                />

                <SearchTh
                  value={columnFilters.car}
                  onChange={(e) =>
                    setColumnFilters({ ...columnFilters, car: e.target.value })
                  }
                />

                <Th p={2}>
                  <Select
                    size="sm"
                    value={columnFilters.bookingType}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        bookingType: e.target.value,
                      })
                    }
                    fontSize="xs"
                    borderRadius="8px"
                    bg="#fff"
                    border="none"
                    _focus={{
                      bg: "white",
                      boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <option value="">All</option>
                    <option value="regular">Regular</option>
                    <option value="package">Package</option>
                    <option value="custom">Custom</option>
                  </Select>
                </Th>

                <Th p={2}>
                  <Select
                    size="sm"
                    value={columnFilters.status}
                    onChange={(e) =>
                      setColumnFilters({
                        ...columnFilters,
                        status: e.target.value,
                      })
                    }
                    fontSize="xs"
                    borderRadius="8px"
                    bg="#fff"
                    border="none"
                    _focus={{
                      bg: "white",
                      boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <option value="">All</option>
                    <option value="new">New</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="complete">Complete</option>
                    <option value="converted">Converted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                    <option value="assigned">Assigned</option>
                    <option value="unassigned">Unassigned</option>
                  </Select>
                </Th>

                {selectedTab !== "Bookings" && <Th></Th>}
              </Tr>
            )}
          </Thead>

          <Tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Tr key={i}>
                  <Td colSpan={12}>
                    <Skeleton height="32px" borderRadius="md" />
                  </Td>
                </Tr>
              ))
            ) : pageRows.length === 0 ? (
              <Tr>
                <Td colSpan={12}>
                  <Flex justify="center" p={6}>
                    <Text color="gray.500">No records found</Text>
                  </Flex>
                </Td>
              </Tr>
            ) : (
              pageRows.map((item, index) => (
                <Tr
                  key={item.id}
                  cursor="pointer"
                  _hover={{
                    bg:
                      vehicleSelected && selectedTab === "Leads"
                        ? "blue.50"
                        : "gray.100",
                  }}
                  onClick={() => {
                    // Always navigate to LeadDetails when clicking a Lead row
                    if (selectedTab === "Leads") {
                      const leadId = item.raw?._id || item.id;
                      // store selectedLeadId for workflows that rely on it
                      localStorage.setItem("selectedLeadId", leadId);
                      navigate(`/bookings/lead/${leadId}`);
                      return;
                    }

                    if (selectedTab === "Bookings") {
                      navigate(`/bookings/${item.raw?._id || item.id}`);
                      return;
                    }

                    if (selectedTab === "Pending") {
                      navigate(`/bookings/pending/${item.raw?._id || item.id}`);
                      return;
                    }

                    if (selectedTab === "Custom Bookings") {
                      navigate(`/bookings/custom/${item.raw?._id || item.id}`);
                      return;
                    }
                  }}
                >
                  <Td>
                    {(usingApi
                      ? (backendPagination.currentPage - 1) *
                      backendPagination.limit
                      : (currentPage - 1) * itemsPerPage) +
                      index +
                      1}
                  </Td>
                  <Td>{item.name}</Td>
                  <Td>{item.mobileNo}</Td>
                  <Td>
                    {item.date && item.date !== "-"
                      ? new Date(item.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                      : "-"}
                  </Td>
                  <Td>
                    <span style={{ whiteSpace: "nowrap" }}>
                      {(() => {
                        if (!item.date || item.date === "-") return "-";
                        const date = new Date(item.date);
                        const hours = date.getHours();
                        const minutes = date
                          .getMinutes()
                          .toString()
                          .padStart(2, "0");
                        const ampm = hours >= 12 ? "PM" : "AM";
                        const displayHours = hours % 12 || 12;
                        return `${displayHours}:${minutes} ${ampm}`;
                      })()}
                    </span>
                  </Td>

                  <Td>{item.price}</Td>
                  <Td>{item.pickUp}</Td>
                  <Td>{item.destination}</Td>
                  <Td>{item.car}</Td>
                  <Td>
                    <Badge
                      colorScheme={
                        item.bookingType === "custom" ? "purple" :
                          item.bookingType === "package" ? "cyan" :
                            "gray"
                      }
                      px={3}
                      py={1}
                      rounded="lg"
                      textTransform="capitalize"
                    >
                      {item.bookingType || "regular"}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        item.status === "Assigned" ? "green" : "orange"
                      }
                      px={3}
                      py={1}
                      rounded="lg"
                    >
                      {item.status}
                    </Badge>
                  </Td>
                  {selectedTab !== "Bookings" && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          {/* View option removed for bookings, leads, and pending tokens */}
                          <MenuItem
                            icon={<FiEdit2 />}
                            onClick={() => {
                              if (selectedTab === "Pending") {
                                navigate(
                                  `/bookings/pending-token/edit/${item.id}`,
                                );
                              } else if (selectedTab === "Bookings") {
                                navigate(`/bookings/edit/${item.id}`);
                              } else if (selectedTab === "Leads") {
                                navigate(`/bookings/lead/edit/${item.id}`);
                              } else if (selectedTab === "Custom Bookings") {
                                navigate(
                                  `/bookings/custom-booking/edit/${item.raw?._id || item.id
                                  }`,
                                );
                              }
                            }}
                          >
                            Edit
                          </MenuItem>
                          {/* Delete option removed for bookings */}
                        </MenuList>
                      </Menu>
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* Pagination */}
        <Flex justifyContent="space-between" alignItems="center" mt={4} gap={2}>
          <Flex align="center" gap={4} wrap="wrap" justify="center" mt={4}>
            <Text fontSize="sm" color={"#8B909A"}>
              Showing:
            </Text>
            <Select
              size="sm"
              width="fit-content"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 20, 50].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color={"#8B909A"}>
              {" "}
              of {totalRows}
            </Text>
          </Flex>

          <Flex alignItems="center" mt={4} gap={2}>
            <Button
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              isDisabled={currentPage === 1}
            >
              <GoChevronLeft />
            </Button>

            {(() => {
              let startPage = 1;
              let endPage = totalPages;

              if (totalPages > 5) {
                if (currentPage <= 3) {
                  startPage = 1;
                  endPage = 5;
                } else if (currentPage >= totalPages - 2) {
                  startPage = totalPages - 4;
                  endPage = totalPages;
                } else {
                  startPage = currentPage - 2;
                  endPage = currentPage + 2;
                }
              }

              const pagesToShow = [];
              for (let i = startPage; i <= endPage; i++) {
                pagesToShow.push(
                  <Button
                    key={i}
                    size="sm"
                    variant={i === currentPage ? "solid" : ""}
                    colorScheme={i === currentPage ? "#0F60FF" : ""}
                    bgColor={i === currentPage ? "#0F60FF" : "#F1F2F6"}
                    onClick={() => setCurrentPage(i)}
                  >
                    <Text color={i === currentPage ? "#FFFFFF" : "#8B909A"}>
                      {i}
                    </Text>
                  </Button>,
                );
              }
              return pagesToShow;
            })()}

            <Button
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages || 1))
              }
              isDisabled={currentPage === totalPages}
            >
              <GoChevronRight />
            </Button>
          </Flex>
        </Flex>
      </Box>


    </Stack>
  );
};

export default Leads;
